import { JiraArrayType, JiraFieldType, JiraSchemaCustomType } from './jira-meta-fields';

export enum $azureRequiredField {
  fieldName = 'fieldName',
  referenceName = 'referenceName',
  type = 'type',
  conditionallyRequired = 'conditionallyRequired',
  description = 'description',
  defaultValue = 'defaultValue',
  isIdentity = 'isIdentity',
  teamMembers = 'teamMembers',
  allowedValues = 'allowedValues',
  alwaysRequired = 'alwaysRequired',
}

export enum $azureUser {
  id = 'id',
  displayName = 'displayName',
  uniqueName = 'uniqueName',
  url = 'url',
}

export enum AzureFieldType {
  String = 0,
  Integer = 1,
  DateTime = 2,
  PlainText = 3,
  Html = 4,
  TreePath = 5,
  History = 6,
  Double = 7,
  Guid = 8,
  Boolean = 9,
  Identity = 10,
  PicklistString = 11,
  PicklistInteger = 12,
  PicklistDouble = 13,
}

/**
 * Link to description: https://developers.asana.com/docs/custom-fields-guide#types-of-custom-fields
 */
export enum AsanaFieldType {
  /**
   * An arbitrary, relatively short string of text
   */
  text = 'text',
  /**
   * A number with a defined level of precision
   */
  number = 'number',
  /**
   * A selection of a single option from a defined list of options (i.e., mutually exclusive selections)
   */
  enum = 'enum',
  /**
   * A selection of one or more options from a defined list of options (i.e., mutually inclusive selections)
   */
  multi_enum = 'multi_enum',
  /**
   * A reference date with an optional time value
   */
  date = 'date',
  /**
   * A list of active contributors (i.e., where their relationship to the work is defined in the custom field title)
   */
  people = 'people',
}

export enum $issueIntegrationRequiredField {
  autoCompleteUrl = 'autoCompleteUrl',
  allowedValues = 'allowedValues',
  hasDefaultValue = 'hasDefaultValue',
  defaultValue = 'defaultValue',
  key = 'key',
  fieldId = 'fieldId',
  normalizedFieldId = 'normalizedFieldId',
  name = 'name',
  operations = 'operations',
  required = 'required',
  schema = 'schema',
  groups = 'groups',
}

export enum $githubConfigurableField {
  labels = 'labels',
  assignees = 'assignees',
  milestone = 'milestone',
}

export enum $gitlabConfigurableField {
  labels = 'labels',
  assignees = 'assignees',
  milestone = 'milestone',
  weight = 'weight',
  startDate = 'startDate',
  dueDate = 'dueDate',
}

export enum GitlabCustomFieldType {
  singleSelect = 'SINGLE_SELECT',
  multiSelect = 'MULTI_SELECT',
  number = 'NUMBER',
  text = 'TEXT',
}

export enum GitlabWidgetType {
  labels = 'LABELS',
  assignees = 'ASSIGNEES',
  milestone = 'MILESTONE',
  weight = 'WEIGHT',
  startAndDueDate = 'START_AND_DUE_DATE',
  customFields = 'CUSTOM_FIELDS',
}

export enum $gitlabCustomFieldDefinition {
  id = 'id',
  name = 'name',
  fieldType = 'fieldType',
  selectOptions = 'selectOptions',
}

export const GITLAB_CUSTOM_FIELD_GID_PREFIX: string = 'gid://gitlab/Issuables::CustomField/';

export const GITHUB_ORGANIZATIONAL_FIELD_PREFIX: string = 'githubOrganizationalField:';

export function toGithubOrganizationalFieldId(fieldId: number): string {
  return `${GITHUB_ORGANIZATIONAL_FIELD_PREFIX}${fieldId}`;
}

export const GITHUB_NO_ISSUE_TYPE_ID: string = 'Issue';

export const GITHUB_PROJECT_FIELD_PREFIX: string = 'githubProjectField:';

export function toGithubProjectFieldId(fieldId: string): string {
  return `${GITHUB_PROJECT_FIELD_PREFIX}${fieldId}`;
}

export const GITHUB_PROJECT_V2_BUCKET_PREFIX: string = 'githubProjectV2:';

export function toGithubProjectV2BucketKey(githubProjectV2Id: string): string {
  return `${GITHUB_PROJECT_V2_BUCKET_PREFIX}${githubProjectV2Id}`;
}

export enum $issueIntegrationFieldAllowedValue {
  id = 'id',
  value = 'value',
  name = 'name',
  self = 'self',
  fieldToBeSaved = 'fieldToBeSaved',
  children = 'children',
  groups = 'groups',
}

export enum $issueIntegrationFieldSchema {
  type = 'type',
  custom = 'custom',
  customId = 'customId',
  items = 'items',
  system = 'system',
}

export enum $jiraField {
  autoCompleteUrl = 'autoCompleteUrl',
  allowedValues = 'allowedValues',
  hasDefaultValue = 'hasDefaultValue',
  defaultValue = 'defaultValue',
  key = 'key',
  fieldId = 'fieldId',
  name = 'name',
  operations = 'operations',
  required = 'required',
  schema = 'schema',
  groups = 'groups',
}

export enum $jiraUser {
  self = 'self',
  accountId = 'accountId',
  name = 'name',
  accountType = 'accountType',
  emailAddress = 'emailAddress',
  avatarUrls = 'avatarUrls',
  displayName = 'displayName',
  active = 'active',
  timeZone = 'timeZone',
  locale = 'locale',
}

export enum EapFieldType {
  any = 'any',
  string = 'string',
  number = 'number',
  boolean = 'boolean',
  option = 'option',
  array = 'array',
  user = 'user',
  project = 'project',
  date = 'date',
  dateTime = 'datetime',
  issueType = 'issuetype',
  optionWithChild = 'option-with-child',
  component = 'component',
  version = 'version',
  priority = 'priority',
  labels = 'labels',
  group = 'group',
  team = 'team',
}

export enum EapArrayItemsType {
  component = 'component',
  option = 'option',
  string = 'string',
  version = 'version',
  user = 'user',
}

export enum EapCustomSchemaType {
  textArea = 'textarea',
  radioButtons = 'radiobuttons',
  url = 'url',
  labels = 'labels',
}

export const GitlabCustomFieldTypeToEapFieldTypeMapping: Record<GitlabCustomFieldType, EapFieldType> = {
  [GitlabCustomFieldType.singleSelect]: EapFieldType.option,
  [GitlabCustomFieldType.multiSelect]: EapFieldType.array,
  [GitlabCustomFieldType.number]: EapFieldType.number,
  [GitlabCustomFieldType.text]: EapFieldType.string,
};

export const AsanaToEapFieldTypeMapping: Record<AsanaFieldType, EapFieldType> = {
  [AsanaFieldType.text]: EapFieldType.string,
  [AsanaFieldType.number]: EapFieldType.number,
  [AsanaFieldType.enum]: EapFieldType.option,
  [AsanaFieldType.multi_enum]: EapFieldType.array,
  [AsanaFieldType.date]: EapFieldType.date,
  [AsanaFieldType.people]: EapFieldType.user,
};

export const AzureToEapFieldTypeMapping: Record<AzureFieldType, EapFieldType> = {
  [AzureFieldType.String]: EapFieldType.string,
  [AzureFieldType.Integer]: EapFieldType.number,
  [AzureFieldType.DateTime]: EapFieldType.dateTime,
  [AzureFieldType.PlainText]: EapFieldType.string,
  [AzureFieldType.Html]: EapFieldType.string,
  [AzureFieldType.TreePath]: EapFieldType.any, // Can't be made required
  [AzureFieldType.History]: EapFieldType.any, // Can't be made required
  [AzureFieldType.Double]: EapFieldType.number,
  [AzureFieldType.Guid]: EapFieldType.string,
  [AzureFieldType.Boolean]: EapFieldType.boolean,
  [AzureFieldType.Identity]: EapFieldType.user,
  [AzureFieldType.PicklistString]: EapFieldType.option,
  [AzureFieldType.PicklistInteger]: EapFieldType.option,
  [AzureFieldType.PicklistDouble]: EapFieldType.option,
};

export const JiraToEapFieldTypeMapping: Record<JiraFieldType, EapFieldType> = {
  [JiraFieldType.any]: EapFieldType.any,
  [JiraFieldType.string]: EapFieldType.string,
  [JiraFieldType.number]: EapFieldType.number,
  [JiraFieldType.option]: EapFieldType.option,
  [JiraFieldType.array]: EapFieldType.array,
  [JiraFieldType.user]: EapFieldType.user,
  [JiraFieldType.project]: EapFieldType.project,
  [JiraFieldType.date]: EapFieldType.date,
  [JiraFieldType.dateTime]: EapFieldType.dateTime,
  [JiraFieldType.issueType]: EapFieldType.issueType,
  [JiraFieldType.optionWithChild]: EapFieldType.optionWithChild,
  [JiraFieldType.component]: EapFieldType.component,
  [JiraFieldType.version]: EapFieldType.version,
  [JiraFieldType.priority]: EapFieldType.priority,
  [JiraFieldType.labels]: EapFieldType.labels,
  [JiraFieldType.group]: EapFieldType.group,
  [JiraFieldType.team]: EapFieldType.team,
};

export const JiraFieldCustomSchemaTypeMapping: Record<JiraSchemaCustomType, EapCustomSchemaType> = {
  [JiraSchemaCustomType.textArea]: EapCustomSchemaType.textArea,
  [JiraSchemaCustomType.radioButtons]: EapCustomSchemaType.radioButtons,
  [JiraSchemaCustomType.url]: EapCustomSchemaType.url,
  [JiraSchemaCustomType.labels]: EapCustomSchemaType.labels,
};

export const JiraArrayFieldSchemaTypeMapping: Record<JiraArrayType, EapArrayItemsType> = {
  [JiraArrayType.component]: EapArrayItemsType.component,
  [JiraArrayType.option]: EapArrayItemsType.option,
  [JiraArrayType.string]: EapArrayItemsType.string,
  [JiraArrayType.version]: EapArrayItemsType.version,
  [JiraArrayType.user]: EapArrayItemsType.user,
};
